import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Zap, Server, Bot, Monitor, Terminal, MessageSquare, Check, X } from 'lucide-react';

import pluginData from '../data/plugins.json';

type Platform = 'cowork' | 'code' | 'chat';

const platforms: { id: Platform; label: string; icon: typeof Monitor }[] = [
  { id: 'cowork', label: 'Cowork', icon: Monitor },
  { id: 'code', label: 'Claude Code', icon: Terminal },
  { id: 'chat', label: 'Chat', icon: MessageSquare },
];

export function HomePage() {
  const [activePlatform, setActivePlatform] = useState<Platform>('cowork');
  const skills = pluginData.plugins.filter((p) => p.type === 'skill');
  const mcps = pluginData.plugins.filter((p) => p.type === 'mcp');
  const agents = pluginData.plugins.filter((p) => p.type === 'agent');

  return (
    <div className="px-6 lg:px-8 py-12">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-foreground mb-4">
          Coretex Marketplace
        </h1>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          Claude plugins built by the Pixacore team. Skills, MCP connectors, and
          agents for Claude Code, Cowork, and Chat.
        </p>
      </div>

      {/* Category Cards */}
      <div className="grid md:grid-cols-3 gap-8 mb-12">
        <Link
          to="/skills"
          className="group block p-8 bg-card rounded-xl border border-border hover:border-primary transition-colors"
        >
          <div className="flex items-center mb-4">
            <div className="p-3 bg-primary/15 rounded-lg">
              <Zap className="w-6 h-6 text-primary" />
            </div>
            <h2 className="ml-4 text-2xl font-semibold text-foreground group-hover:text-primary">
              Skills
            </h2>
            <span className="ml-auto text-sm text-muted-foreground">{skills.length}</span>
          </div>
          <p className="text-muted-foreground">
            Extend Claude with custom commands and automation. Skills add
            new capabilities through slash commands and hooks.
          </p>
        </Link>

        <Link
          to="/mcps"
          className="group block p-8 bg-card rounded-xl border border-border hover:border-green-500 transition-colors"
        >
          <div className="flex items-center mb-4">
            <div className="p-3 bg-green-500/15 rounded-lg">
              <Server className="w-6 h-6 text-green-400" />
            </div>
            <h2 className="ml-4 text-2xl font-semibold text-foreground group-hover:text-green-400">
              MCP Servers
            </h2>
            <span className="ml-auto text-sm text-muted-foreground">{mcps.length}</span>
          </div>
          <p className="text-muted-foreground">
            Connect Claude to external services and APIs. MCP servers
            provide tools, resources, and integrations.
          </p>
        </Link>

        <Link
          to="/agents"
          className="group block p-8 bg-card rounded-xl border border-border hover:border-purple-500 transition-colors"
        >
          <div className="flex items-center mb-4">
            <div className="p-3 bg-purple-500/15 rounded-lg">
              <Bot className="w-6 h-6 text-purple-400" />
            </div>
            <h2 className="ml-4 text-2xl font-semibold text-foreground group-hover:text-purple-400">
              Agents
            </h2>
            <span className="ml-auto text-sm text-muted-foreground">{agents.length}</span>
          </div>
          <p className="text-muted-foreground">
            Autonomous specialists that execute complex multi-step tasks.
            Agents work independently with their own context and tools.
          </p>
        </Link>
      </div>

      {/* Getting Started */}
      <div className="bg-card rounded-xl border border-primary/30 p-8 mb-12">
        <h2 className="text-2xl font-semibold text-foreground mb-2">
          Getting Started
        </h2>
        <p className="text-muted-foreground mb-6">
          Coretex plugins work across Claude platforms. Choose yours to see setup instructions.
        </p>

        {/* Tab Bar */}
        <div className="flex border-b border-border mb-6">
          {platforms.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActivePlatform(id)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${
                activePlatform === id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="min-h-[280px]">
          {activePlatform === 'cowork' && (
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-foreground mb-3">Setup</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Cowork provides a built-in plugin marketplace. Your organization admin has made Coretex plugins available — browse and install the ones relevant to your work.
                </p>
                <ol className="space-y-3 text-sm text-muted-foreground">
                  <li className="flex items-start">
                    <span className="text-primary font-medium mr-2">1.</span>
                    <span>In your Cowork project, open <strong className="text-foreground">Customize</strong> from the sidebar</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary font-medium mr-2">2.</span>
                    <span>Click <strong className="text-foreground">Browse plugins</strong> to see the available Coretex plugins</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary font-medium mr-2">3.</span>
                    <span>Click <strong className="text-foreground">Install</strong> on any plugin you'd like to use</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary font-medium mr-2">4.</span>
                    <span>Once installed, enable the plugin in your project settings to start using it</span>
                  </li>
                </ol>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-3">What's supported</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Check className="w-4 h-4 text-green-400 shrink-0" />
                    <span><strong className="text-foreground">Skills</strong> — slash commands like /migrate-pptx</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Check className="w-4 h-4 text-green-400 shrink-0" />
                    <span><strong className="text-foreground">Agents</strong> — autonomous specialists like PowerPoint Expert</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Check className="w-4 h-4 text-green-400 shrink-0" />
                    <span><strong className="text-foreground">MCP Connectors</strong> — tools like SharePoint search</span>
                  </div>
                </div>
                <div className="mt-4 bg-background rounded-lg p-4 border border-border">
                  <p className="text-xs text-muted-foreground">
                    Cowork is the easiest way to get started. Plugins installed here also become available in Claude Chat through the Connectors feature.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activePlatform === 'code' && (
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-foreground mb-3">Setup</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Claude Code connects to the Coretex Marketplace through its private GitHub repository. Once added, all plugins are available to install directly from the CLI.
                </p>
                <ol className="space-y-3 text-sm text-muted-foreground">
                  <li className="flex items-start">
                    <span className="text-green-400 font-medium mr-2">1.</span>
                    <span>Open Claude Code and type <strong className="text-foreground">/plugins</strong>, then select <strong className="text-foreground">Manage marketplace sources</strong></span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-400 font-medium mr-2">2.</span>
                    <span>Add the Coretex marketplace repository:</span>
                  </li>
                </ol>
                <div className="mt-3 bg-background rounded-lg p-3 font-mono text-xs text-foreground border border-border">
                  <p className="text-muted-foreground"># Add the private marketplace</p>
                  <p>pixacore-research/Coretex-Marketplace</p>
                </div>
                <ol start={3} className="mt-3 space-y-3 text-sm text-muted-foreground">
                  <li className="flex items-start">
                    <span className="text-green-400 font-medium mr-2">3.</span>
                    <span>Browse and install plugins with <strong className="text-foreground">/plugins</strong> → <strong className="text-foreground">Install a plugin</strong></span>
                  </li>
                </ol>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-3">What's supported</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Check className="w-4 h-4 text-green-400 shrink-0" />
                    <span><strong className="text-foreground">Skills</strong> — slash commands with full local tool access</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Check className="w-4 h-4 text-green-400 shrink-0" />
                    <span><strong className="text-foreground">Agents</strong> — autonomous subagents with isolated context</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Check className="w-4 h-4 text-green-400 shrink-0" />
                    <span><strong className="text-foreground">MCP Servers</strong> — local and remote tool connections</span>
                  </div>
                </div>
                <div className="mt-4 bg-background rounded-lg p-4 border border-border">
                  <p className="text-xs text-muted-foreground">
                    Claude Code provides the most complete plugin experience with direct file system access, local MCP servers, and full agent capabilities. Requires GitHub access to the private repository.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activePlatform === 'chat' && (
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-foreground mb-3">Setup</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Claude Chat on claude.ai supports MCP Connectors and skills that have been installed through Cowork. No separate setup is needed — plugins you install in Cowork automatically become available in Chat.
                </p>
                <ol className="space-y-3 text-sm text-muted-foreground">
                  <li className="flex items-start">
                    <span className="text-purple-400 font-medium mr-2">1.</span>
                    <span>Install the plugin in <strong className="text-foreground">Cowork</strong> first (see the Cowork tab)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-400 font-medium mr-2">2.</span>
                    <span>Open <strong className="text-foreground">claude.ai</strong> and start a new conversation</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-400 font-medium mr-2">3.</span>
                    <span>MCP tools appear as <strong className="text-foreground">Connectors</strong> — Claude uses them automatically when relevant</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-400 font-medium mr-2">4.</span>
                    <span>Skills are available as slash commands in the chat input</span>
                  </li>
                </ol>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-3">What's supported</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Check className="w-4 h-4 text-green-400 shrink-0" />
                    <span><strong className="text-foreground">Skills</strong> — installed via Cowork</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Check className="w-4 h-4 text-green-400 shrink-0" />
                    <span><strong className="text-foreground">MCP Connectors</strong> — installed via Cowork</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <X className="w-4 h-4 text-muted-foreground/50 shrink-0" />
                    <span className="text-muted-foreground/70"><strong>Agents</strong> — not available in Chat, use Cowork or Claude Code</span>
                  </div>
                </div>
                <div className="mt-4 bg-background rounded-lg p-4 border border-border">
                  <p className="text-xs text-muted-foreground">
                    Chat is ideal for quick access to MCP Connectors like SharePoint search without leaving the browser. For advanced workflows involving agents or file operations, use Cowork or Claude Code.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Educational Sections */}
      <div className="grid md:grid-cols-3 gap-8 mb-12">
        {/* What is a Skill? */}
        <div className="bg-card rounded-xl border border-border p-8">
          <div className="flex items-center mb-4">
            <div className="p-3 bg-primary/15 rounded-lg">
              <Zap className="w-6 h-6 text-primary" />
            </div>
            <h2 className="ml-4 text-xl font-semibold text-foreground">
              What is a Skill?
            </h2>
          </div>
          <p className="text-muted-foreground mb-4">
            Skills are instruction sets that teach Claude how to perform specific tasks.
            They act like "specialized training manuals" that give Claude expertise on demand.
          </p>
          <ul className="text-sm text-muted-foreground space-y-2 mb-4">
            <li className="flex items-start">
              <span className="text-primary mr-2">•</span>
              Invoked via slash commands (e.g., /migrate-pptx)
            </li>
            <li className="flex items-start">
              <span className="text-primary mr-2">•</span>
              Contain step-by-step workflows and decision logic
            </li>
            <li className="flex items-start">
              <span className="text-primary mr-2">•</span>
              Can include helper scripts for complex operations
            </li>
            <li className="flex items-start">
              <span className="text-primary mr-2">•</span>
              Available in Claude Code, Cowork, and Chat
            </li>
          </ul>
          <div className="bg-background rounded-lg p-4 font-mono text-sm text-foreground border border-border">
            <p className="text-muted-foreground mb-1"># Example: @pixacore/pptx-template-migration</p>
            <p className="text-green-400">/migrate-pptx</p>
            <p className="text-muted-foreground mt-2 text-xs">
              Analyzes your deck → Maps to new template → Transforms slides → Validates output
            </p>
          </div>
        </div>

        {/* What is an MCP Server? */}
        <div className="bg-card rounded-xl border border-border p-8">
          <div className="flex items-center mb-4">
            <div className="p-3 bg-green-500/15 rounded-lg">
              <Server className="w-6 h-6 text-green-400" />
            </div>
            <h2 className="ml-4 text-xl font-semibold text-foreground">
              What is an MCP Server?
            </h2>
          </div>
          <p className="text-muted-foreground mb-4">
            MCP (Model Context Protocol) servers connect Claude to external tools and services.
            In Chat, these appear as Connectors. They provide standardized bridges to APIs, databases, and other systems.
          </p>
          <ul className="text-sm text-muted-foreground space-y-2 mb-4">
            <li className="flex items-start">
              <span className="text-green-400 mr-2">•</span>
              Expose tools that Claude can call directly
            </li>
            <li className="flex items-start">
              <span className="text-green-400 mr-2">•</span>
              Handle authentication and API communication
            </li>
            <li className="flex items-start">
              <span className="text-green-400 mr-2">•</span>
              Available as Connectors in Chat via Cowork
            </li>
            <li className="flex items-start">
              <span className="text-green-400 mr-2">•</span>
              Provide resources and data access
            </li>
          </ul>
          <div className="bg-background rounded-lg p-4 font-mono text-sm text-foreground border border-border">
            <p className="text-muted-foreground mb-1"># Example: @pixacore/coretex-sharepoint</p>
            <p className="text-green-400">sharepoint_search</p>
            <p className="text-green-400">sharepoint_download</p>
            <p className="text-muted-foreground mt-2 text-xs">
              Claude uses these tools automatically when you ask about SharePoint content
            </p>
          </div>
        </div>

        {/* What is an Agent? */}
        <div className="bg-card rounded-xl border border-border p-8">
          <div className="flex items-center mb-4">
            <div className="p-3 bg-purple-500/15 rounded-lg">
              <Bot className="w-6 h-6 text-purple-400" />
            </div>
            <h2 className="ml-4 text-xl font-semibold text-foreground">
              What is an Agent?
            </h2>
          </div>
          <p className="text-muted-foreground mb-4">
            Agents are autonomous specialists that execute complex multi-step tasks.
            Think of them as "skilled colleagues" that work independently with their own context.
          </p>
          <ul className="text-sm text-muted-foreground space-y-2 mb-4">
            <li className="flex items-start">
              <span className="text-purple-400 mr-2">•</span>
              Run autonomously with isolated execution context
            </li>
            <li className="flex items-start">
              <span className="text-purple-400 mr-2">•</span>
              Propose actions at checkpoints for user approval
            </li>
            <li className="flex items-start">
              <span className="text-purple-400 mr-2">•</span>
              Can orchestrate multiple skills and MCP tools
            </li>
            <li className="flex items-start">
              <span className="text-purple-400 mr-2">•</span>
              Available in Claude Code and Cowork
            </li>
          </ul>
          <div className="bg-background rounded-lg p-4 font-mono text-sm text-foreground border border-border">
            <p className="text-muted-foreground mb-1"># Example: @pixacore/powerpoint-expert</p>
            <p className="text-purple-400">Analyze → Plan → Execute → Report</p>
            <p className="text-muted-foreground mt-2 text-xs">
              Processes presentations autonomously, pausing at key decision points
            </p>
          </div>
        </div>
      </div>

      {/* Available Packages */}
      <div className="bg-card rounded-xl border border-border p-8">
        <h2 className="text-2xl font-semibold text-foreground mb-4">
          Available Packages
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <h3 className="font-medium text-foreground mb-2">Skills</h3>
            <ul className="space-y-1 text-sm">
              {skills.map((p) => (
                <li key={p.name} className="flex items-center text-muted-foreground">
                  <span className="w-2 h-2 bg-primary rounded-full mr-2 shrink-0"></span>
                  <Link to="/skills" className="hover:text-foreground transition-colors">
                    {p.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-medium text-foreground mb-2">MCP Servers</h3>
            <ul className="space-y-1 text-sm">
              {mcps.map((p) => (
                <li key={p.name} className="flex items-center text-muted-foreground">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2 shrink-0"></span>
                  <Link to="/mcps" className="hover:text-foreground transition-colors">
                    {p.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-medium text-foreground mb-2">Agents</h3>
            <ul className="space-y-1 text-sm">
              {agents.map((p) => (
                <li key={p.name} className="flex items-center text-muted-foreground">
                  <span className="w-2 h-2 bg-purple-500 rounded-full mr-2 shrink-0"></span>
                  <Link to="/agents" className="hover:text-foreground transition-colors">
                    {p.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
